(() => {
  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const initYears = (scope = document) => {
    scope.querySelectorAll('[data-pa-year]').forEach((node) => {
      node.textContent = String(new Date().getFullYear());
    });
  };

  const initSlider = (root) => {
    if (!root || root.dataset.paInitialized === 'true') return;

    const track = root.querySelector('[data-pa-slider-track]');
    const slides = Array.from(root.querySelectorAll('[data-pa-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-pa-dot]'));
    const previous = root.querySelector('[data-pa-prev]');
    const next = root.querySelector('[data-pa-next]');
    const pause = root.querySelector('[data-pa-pause]');

    if (!track || slides.length === 0) return;
    root.dataset.paInitialized = 'true';

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const desktop = window.matchMedia('(min-width: 990px)');
    const autoplayRequested = root.dataset.autoplay === 'true';
    const speed = Number.parseInt(root.dataset.speed || '9000', 10);

    let current = 0;
    let timer = null;
    let paused = false;
    let startX = null;
    let dragX = 0;

    const activeSlide = () => slides[current];

    const setTrackHeight = () => {
      window.requestAnimationFrame(() => {
        const slide = activeSlide();
        if (!slide) return;
        const measured = Math.ceil(slide.scrollHeight || slide.getBoundingClientRect().height || 0);
        const minimum = desktop.matches ? 540 : 0;
        track.style.height = `${Math.max(measured, minimum)}px`;
      });
    };

    const stop = () => {
      if (timer) window.clearInterval(timer);
      timer = null;
    };

    const start = () => {
      stop();
      if (!autoplayRequested || paused || reducedMotion.matches || slides.length < 2) return;
      timer = window.setInterval(() => show(current + 1, false), Math.max(speed, 7000));
    };

    const updatePause = () => {
      if (!pause) return;
      pause.classList.toggle('is-paused', paused);
      pause.setAttribute('aria-pressed', String(paused));
      pause.setAttribute('aria-label', paused ? 'Reanudar carrusel' : 'Pausar carrusel');
    };

    const show = (target, restart = true) => {
      current = (target + slides.length) % slides.length;

      slides.forEach((slide, index) => {
        slide.style.removeProperty('transition');
        slide.style.removeProperty('transform');
        slide.style.removeProperty('opacity');
        const active = index === current;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });

      dots.forEach((dot, index) => {
        const active = index === current;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      setTrackHeight();
      window.setTimeout(setTrackHeight, 80);
      if (restart) start();
    };

    previous?.addEventListener('click', () => show(current - 1));
    next?.addEventListener('click', () => show(current + 1));
    dots.forEach((dot, index) => dot.addEventListener('click', () => show(index)));

    pause?.addEventListener('click', () => {
      paused = !paused;
      updatePause();
      start();
    });

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) start();
    });

    root.addEventListener('pointerdown', (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      if (event.target.closest('a, button, input, select, textarea, video')) return;
      stop();
      startX = event.clientX;
      dragX = 0;
      root.classList.add('is-swiping');
      activeSlide()?.setPointerCapture?.(event.pointerId);
    });

    root.addEventListener('pointermove', (event) => {
      if (startX === null) return;
      dragX = Math.max(-150, Math.min(150, event.clientX - startX));
      if (Math.abs(dragX) > 6) event.preventDefault();
      const slide = activeSlide();
      if (!slide) return;
      slide.style.transition = 'none';
      slide.style.transform = `translateX(${dragX}px) scale(.992)`;
      slide.style.opacity = String(Math.max(.62, 1 - Math.abs(dragX) / 360));
    });

    const finishSwipe = (event) => {
      if (startX === null) return;
      const distance = dragX || event.clientX - startX;
      const slide = activeSlide();
      startX = null;
      dragX = 0;
      root.classList.remove('is-swiping');
      if (slide) {
        slide.style.removeProperty('transition');
        slide.style.removeProperty('transform');
        slide.style.removeProperty('opacity');
      }
      if (Math.abs(distance) >= 48) show(distance > 0 ? current - 1 : current + 1);
      else start();
    };

    root.addEventListener('pointerup', finishSwipe);
    root.addEventListener('pointercancel', finishSwipe);

    root.querySelectorAll('img, video').forEach((media) => {
      media.addEventListener('load', setTrackHeight, { once: true });
      media.addEventListener('loadedmetadata', setTrackHeight, { once: true });
    });

    window.addEventListener('resize', setTrackHeight, { passive: true });
    desktop.addEventListener?.('change', setTrackHeight);
    reducedMotion.addEventListener?.('change', start);

    updatePause();
    show(0, false);
    start();
  };

  const initMaquilaModal = (scope = document) => {
    const modal = document.querySelector('[data-pa-maquila-modal]');
    if (!modal || modal.dataset.paInitialized === 'true') return;
    modal.dataset.paInitialized = 'true';

    const openers = Array.from(document.querySelectorAll('[data-pa-open-maquila]'));
    const closers = Array.from(modal.querySelectorAll('[data-pa-close-maquila]'));
    const video = modal.querySelector('video');
    let lastFocused = null;

    const open = (event) => {
      event?.preventDefault();
      lastFocused = document.activeElement;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('pa-modal-open');
      modal.querySelector('[data-pa-close-maquila]')?.focus();
    };

    const close = (event) => {
      event?.preventDefault();
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('pa-modal-open');
      if (video && !video.paused) video.pause();
      lastFocused?.focus?.();
    };

    openers.forEach((opener) => opener.addEventListener('click', open));
    closers.forEach((closer) => closer.addEventListener('click', close));

    modal.addEventListener('click', (event) => {
      if (event.target === modal) close(event);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('is-open')) close(event);
    });

    const autoOpen = modal.dataset.autoOpen === 'true';
    const delay = Number.parseInt(modal.dataset.delay || '12000', 10);
    const sessionKey = 'pa-maquila-popup-v2';

    if (autoOpen && !sessionStorage.getItem(sessionKey)) {
      window.setTimeout(() => {
        if (!modal.classList.contains('is-open')) {
          open();
          sessionStorage.setItem(sessionKey, 'shown');
        }
      }, Math.max(delay, 2000));
    }
  };


  const initVideoCovers = (scope = document) => {
    scope.querySelectorAll('[data-pa-video-cover]').forEach((cover) => {
      if (cover.dataset.paInitialized === 'true') return;
      cover.dataset.paInitialized = 'true';
      const frame = cover.closest('.pa-future-video');
      const video = frame?.querySelector('[data-pa-main-video]');
      if (!video) return;
      cover.addEventListener('click', async () => {
        cover.classList.add('is-hidden');
        try { await video.play(); } catch (error) { cover.classList.remove('is-hidden'); }
      });
    });
  };

  const initRelatedCarousels = (scope = document) => {
    scope.querySelectorAll('[data-pa-related-carousel]').forEach((viewport) => {
      if (viewport.dataset.paInitialized === 'true') return;
      viewport.dataset.paInitialized = 'true';
      const section = viewport.closest('.pa-related');
      const previous = section?.querySelector('[data-pa-related-prev]');
      const next = section?.querySelector('[data-pa-related-next]');
      const distance = () => Math.max(viewport.clientWidth * .78, 260);
      previous?.addEventListener('click', () => viewport.scrollBy({ left: -distance(), behavior: 'smooth' }));
      next?.addEventListener('click', () => viewport.scrollBy({ left: distance(), behavior: 'smooth' }));
    });
  };

  const initAll = (scope = document) => {
    initYears(scope);
    scope.querySelectorAll('[data-pa-future-slider]').forEach(initSlider);
    initMaquilaModal(scope);
    initVideoCovers(scope);
    initRelatedCarousels(scope);
  };

  ready(() => {
    initAll(document);
    const observer = new MutationObserver(() => initRelatedCarousels(document));
    observer.observe(document.body, { childList: true, subtree: true });
  });
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
